import React, { useState, useEffect, useRef, useCallback } from 'react';
// import jsQR from 'jsqr';  // QR scanning — commented out, may re-enable later
import api, { startGeoPingIfCheckedIn, stopGeoPing } from './api';
import useAuth from '../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import LeaveUpdates from './LeaveUpdates';

/* ── Authenticated image loader ─── */
function SecureImg({ src, alt, style }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const urlRef = useRef(null);
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    api.get(src, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        const u = URL.createObjectURL(res.data);
        urlRef.current = u;
        setBlobUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [src]);
  if (!blobUrl) return null;
  return <img src={blobUrl} alt={alt} style={style} />;
}

/* ─── Haversine distance in metres ─── */
function distanceMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

/* ─── QR payload parser — COMMENTED OUT (keep for future QR re-enable) ───
function parseQRPayload(raw) {
  const text = raw.trim();
  function extractCoords(obj) {
    const lat = parseFloat(obj.siteLat ?? obj.lat ?? obj.latitude ?? obj.Lat ?? obj.LAT ?? NaN);
    const lng = parseFloat(obj.siteLng ?? obj.siteLon ?? obj.lng ?? obj.lon
                          ?? obj.longitude ?? obj.Lng ?? obj.LON ?? NaN);
    return { lat, lng };
  }
  function fromObj(obj) {
    const { lat, lng } = extractCoords(obj);
    if (!isFinite(lat) || !isFinite(lng)) throw new Error('no_coords');
    return {
      branchLat:  lat, branchLng:  lng,
      branchName: obj.branchName || obj.name || obj.branch || '',
      branchId:   obj.branchId  || obj.branch || '',
    };
  }
  const jwtParts = text.split('.');
  if (jwtParts.length === 3) {
    try {
      const payload = JSON.parse(atob(jwtParts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return fromObj(payload);
    } catch {}
  }
  if (text.startsWith('{')) { try { return fromObj(JSON.parse(text)); } catch {} }
  try {
    const decoded = atob(text.replace(/-/g, '+').replace(/_/g, '/'));
    if (decoded.startsWith('{')) return fromObj(JSON.parse(decoded));
  } catch {}
  try {
    const url = new URL(text.includes('://') ? text : `https://x.x/?${text}`);
    const q = Object.fromEntries(url.searchParams.entries());
    if (Object.keys(q).length) return fromObj(q);
  } catch {}
  const csv = text.split(',').map(s => s.trim());
  if (csv.length >= 2) {
    const lat = parseFloat(csv[0]), lng = parseFloat(csv[1]);
    if (isFinite(lat) && isFinite(lng))
      return { branchLat: lat, branchLng: lng, branchName: csv[2] || '', branchId: csv[2] || '' };
  }
  throw new Error('Unrecognised QR code format.');
}
─── END QR parser comment ─── */

/* ─── Build calendar grid for a given IST month offset ─── */
function buildCalendar(offset) {
  const now = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + offset;
  const first = new Date(Date.UTC(year, month, 1));
  const last  = new Date(Date.UTC(year, month + 1, 0));
  const days = [];
  for (let i = 0; i < first.getUTCDay(); i++) days.push(null);
  for (let d = 1; d <= last.getUTCDate(); d++) {
    days.push(new Date(Date.UTC(year, month, d)).toISOString().slice(0, 10));
  }
  return {
    days,
    monthLabel: first.toLocaleString('default', { month: 'long', year: 'numeric' }),
  };
}

/* ─── Keyframe CSS ─── */
const STYLES = `
@keyframes pulseRing {
  0%   { box-shadow: 0 0 0 0 rgba(67,160,71,0.4); }
  70%  { box-shadow: 0 0 0 18px rgba(67,160,71,0); }
  100% { box-shadow: 0 0 0 0 rgba(67,160,71,0); }
}
@keyframes slideUp {
  from { opacity:0; transform: translateY(20px); }
  to   { opacity:1; transform: translateY(0); }
}
@keyframes shake {
  0%,100% { transform: translateX(0); }
  20%,60% { transform: translateX(-8px); }
  40%,80% { transform: translateX(8px); }
}
@keyframes popIn {
  0%   { opacity:0; transform: scale(0.85); }
  70%  { transform: scale(1.04); }
  100% { opacity:1; transform: scale(1); }
}
@keyframes shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
`;

export default function AttendanceScan() {
  const { auth } = useAuth();
  const username = auth?.username;
  const displayName = auth?.name || auth?.username || '';
  const canSeeHours = (auth?.roles || []).some(r => ['owner', 'solution', 'supervisor'].includes(r));
  /* ─── tab ─── */
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'leaves' ? 'leaves' : 'checkin');

  /* ─── state ─── */
  const [todayRecord, setTodayRecord] = useState(undefined);
  const [statusMsg, setStatusMsg]     = useState('');
  const [busy, setBusy]               = useState(false);
  const [myHistory, setMyHistory]     = useState([]);
  const [calOffset, setCalOffset]     = useState(0);

  // Selfie flow states
  const [showWelcome, setShowWelcome]     = useState(false);
  const [selfieMode, setSelfieMode]       = useState(false);   // camera open for selfie
  const [selfiePreview, setSelfiePreview] = useState(null);     // base64 captured
  const [uploading, setUploading]         = useState(false);
  // Checkout selfie flow
  const [checkoutSelfieMode, setCheckoutSelfieMode] = useState(false);
  const [checkoutSelfiePreview, setCheckoutSelfiePreview] = useState(null);

  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(document.createElement('canvas'));

  /* ── QR scanning refs — commented out ──
  const scanningRef = useRef(false);
  const rafRef      = useRef(null);
  ── */

  const nowIST   = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  const isSunday = nowIST.getUTCDay() === 0;
  const todayStr = nowIST.toISOString().slice(0, 10);

  /* ─── on mount ─── */
  useEffect(() => {
    if (!username) return;
    fetchToday();
    fetchHistory();
  }, [username]);

  const fetchToday = async () => {
    try {
      const { data } = await api.get(`${process.env.REACT_APP_URL}/myattendance/today/${username}`);
      setTodayRecord(data);
    } catch { setTodayRecord(null); }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await api.get(`${process.env.REACT_APP_URL}/myattendance/myrecords/${username}`);
      setMyHistory(data || []);
    } catch {}
  };

  /* ─── Camera helpers (selfie — front camera) ─── */
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setSelfieMode(false);
  }, []);

  const startSelfieCamera = async () => {
    setSelfieMode(true);
    setSelfiePreview(null);
    setStatusMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setStatusMsg('Camera access denied. Please allow camera permission.');
      setSelfieMode(false);
    }
  };

  const captureSelfie = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // Mirror the image for natural selfie look
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    setSelfiePreview(dataUrl);
    stopCamera();
  };

  const retakeSelfie = () => {
    setSelfiePreview(null);
    startSelfieCamera();
  };

  /* ─── Check-In (no QR) ─── */
  const handleCheckIn = async () => {
    setBusy(true);
    setStatusMsg('Getting your location...');
    let userLat, userLng;
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, {
          enableHighAccuracy: true, timeout: 10000, maximumAge: 0
        })
      );
      userLat = pos.coords.latitude;
      userLng = pos.coords.longitude;
    } catch {
      // For remote users location is optional
      if (!auth.allowedOutside) {
        setStatusMsg('Could not get your location. Please enable GPS and try again.');
        setBusy(false);
        return;
      }
    }
    try {
      const { data } = await api.post(`${process.env.REACT_APP_URL}/myattendance/checkin-simple`, {
        username, userLat, userLng
      });
      setStatusMsg('Checked in successfully!');
      await fetchToday();
      await fetchHistory();
      startGeoPingIfCheckedIn();
      // Show welcome popup for selfie
      setShowWelcome(true);
    } catch (err) {
      setStatusMsg(err.response?.data?.error || 'Check-in failed');
    } finally {
      setBusy(false);
    }
  };

  /* ─── Upload selfie ─── */
  const handleUploadSelfie = async () => {
    if (!selfiePreview) return;
    setUploading(true);
    try {
      await api.post(`${process.env.REACT_APP_URL}/myattendance/selfie`, {
        username,
        image: selfiePreview,
      });
      setStatusMsg('Selfie uploaded — attendance marked!');
      setSelfiePreview(null);
      await fetchToday();
    } catch (err) {
      setStatusMsg(err.response?.data?.error || 'Failed to upload selfie');
    } finally {
      setUploading(false);
    }
  };

  /* ── QR scanning flow — COMMENTED OUT (keep for future use) ──
  const [scanning, setScanning] = useState(false);
  const startCamera = async () => {
    setStatusMsg('');
    setScanning(true);
    scanningRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      tickScan();
    } catch {
      setStatusMsg('Camera access denied.');
      setScanning(false);
    }
  };
  const tickScan = () => {
    if (!scanningRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(tickScan); return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, canvas.width, canvas.height, { inversionAttempts: 'attemptBoth' });
    if (code?.data) { scanningRef.current = false; stopCamera(); handleQRFound(code.data); }
    else { rafRef.current = requestAnimationFrame(tickScan); }
  };
  const handleQRFound = async (qrToken) => {
    // ... QR check-in logic ...
  };
  ── END QR scanning comment ── */

  /* ─── Checkout selfie helpers ─── */
  const startCheckoutSelfie = () => {
    setCheckoutSelfieMode(true);
    setCheckoutSelfiePreview(null);
    setStatusMsg('');
    startSelfieCamera();
  };

  const captureCheckoutSelfie = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    setCheckoutSelfiePreview(dataUrl);
    stopCamera();
  };

  const retakeCheckoutSelfie = () => {
    setCheckoutSelfiePreview(null);
    startSelfieCamera();
  };

  const cancelCheckoutSelfie = () => {
    setCheckoutSelfieMode(false);
    setCheckoutSelfiePreview(null);
    stopCamera();
  };

  /* ─── Check-out (with selfie) ─── */
  const handleCheckOutWithSelfie = async () => {
    setBusy(true);
    setStatusMsg('');
    let userLat, userLng;
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 })
      );
      userLat = pos.coords.latitude;
      userLng = pos.coords.longitude;
    } catch {}
    try {
      const { data } = await api.post(`${process.env.REACT_APP_URL}/myattendance/checkout`, {
        username, userLat, userLng, image: checkoutSelfiePreview
      });
      setStatusMsg(data.message);
      setCheckoutSelfieMode(false);
      setCheckoutSelfiePreview(null);
      stopGeoPing();
      await fetchToday();
      await fetchHistory();
    } catch (err) {
      setStatusMsg(err.response?.data?.error || 'Check-out failed');
    } finally {
      setBusy(false);
    }
  };

  /* ─── Check-out (legacy, no selfie — for confirm-only) ─── */
  const handleCheckOut = async () => {
    // Prompt checkout selfie instead of direct checkout
    startCheckoutSelfie();
  };

  /* ─── derived values ─── */
  const isCheckedIn    = todayRecord?.status === 'IN_PROGRESS';
  const isCompleted    = todayRecord?.status === 'COMPLETED';
  const hasSelfie      = !!todayRecord?.selfieUrl;
  const needsSelfie    = isCheckedIn && !hasSelfie;
  const needsReupload  = isCheckedIn && !hasSelfie && todayRecord?.uniformStatus === 'reupload';

  // 30-min minimum before checkout is allowed
  const lastShift = todayRecord?.shifts?.length ? todayRecord.shifts[todayRecord.shifts.length - 1] : null;
  const lastCheckInTime = lastShift?.checkIn ? new Date(lastShift.checkIn).getTime() : (todayRecord?.checkInTime ? new Date(todayRecord.checkInTime).getTime() : 0);
  const minsSinceCheckIn = lastCheckInTime ? Math.floor((Date.now() - lastCheckInTime) / 60000) : 999;
  const checkoutAllowed = minsSinceCheckIn >= 30;

  const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

  const historyMap = {};
  myHistory.forEach(r => { historyMap[r.date] = r; });
  const { days, monthLabel } = buildCalendar(calOffset);

  function dayMeta(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00Z');
    const dow = d.getUTCDay();
    const rec = historyMap[dateStr];
    const isFuture = dateStr > todayStr;
    const isToday  = dateStr === todayStr;
    if (rec?.status === 'COMPLETED') return { bg: '#43a047', color: '#fff', icon: '\u2713', tip: `${fmtTime(rec.checkInTime)} \u2013 ${fmtTime(rec.checkOutTime)}`, ot: rec.isSunday };
    if (rec?.status === 'IN_PROGRESS') return { bg: '#1976d2', color: '#fff', icon: '\u25CF', tip: `In: ${fmtTime(rec.checkInTime)}`, ot: false };
    if (isFuture) return { bg: 'transparent', color: '#bbb', icon: String(d.getUTCDate()), tip: '' };
    if (isToday && !rec) return { bg: '#fff8e1', color: '#f57c00', icon: String(d.getUTCDate()), tip: 'Not checked in yet', border: '2px solid #ffc107' };
    if (dow === 0) return { bg: '#fff3e0', color: '#ff9800', icon: '\u2600', tip: 'Sunday' };
    if (dow === 6) return { bg: '#f3e5f5', color: '#9c27b0', icon: '\u00BD', tip: 'Saturday' };
    return { bg: '#ffebee', color: '#e53935', icon: '\u2715', tip: 'Absent' };
  }

  const isOkMsg  = statusMsg.includes('successfully') || statusMsg.includes('marked');
  const isErrMsg = statusMsg.includes('denied') || statusMsg.includes('failed') || statusMsg.includes('not within') || statusMsg.includes('Could not') || statusMsg.includes('not found') || statusMsg.includes('not active');

  /* ─────────────────── RENDER ─────────────────── */
  return (
    <>
      <style>{STYLES}</style>

      {/* ══ TAB BAR ══ */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '12px 12px 0' }}>
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0' }}>
          {[
            { key: 'checkin', label: 'Check-in', icon: '\uD83D\uDCCD' },
            { key: 'leaves',  label: 'Leaves',   icon: '\uD83C\uDFD6\uFE0F' },
          ].map(function (tab) {
            var isActive = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={function () { setActiveTab(tab.key); }}
                style={{
                  padding: '10px 24px', fontSize: 14, fontWeight: isActive ? 700 : 600,
                  color: isActive ? '#1d4ed8' : '#64748b', background: 'none', border: 'none',
                  borderBottom: isActive ? '2.5px solid #2563eb' : '2.5px solid transparent',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  marginBottom: -2, transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: 16 }}>{tab.icon}</span> {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ══ LEAVES TAB ══ */}
      {activeTab === 'leaves' && <LeaveUpdates />}

      {/* ══ CHECK-IN TAB ══ */}
      {activeTab === 'checkin' && (
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '16px 12px', fontFamily: "'Segoe UI', sans-serif" }}>

        {/* ══ HEADER ══ */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '2.2rem', marginBottom: 4 }}>{'\uD83C\uDFE2'}</div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1a237e' }}>
            My Attendance
            {isSunday ? <span style={{ color: '#ff9800', fontSize: '0.85em' }}> — Sunday OT</span> : ''}
          </h2>
          <div style={{ color: '#888', fontSize: '0.82em', marginTop: 2 }}>{todayStr}</div>
        </div>

        {/* ══ RE-UPLOAD SELFIE BANNER ══ */}
        {needsReupload && (
          <div style={{
            background: 'linear-gradient(135deg,#fef3c7,#fde68a)',
            border: '1.5px solid #f59e0b', borderRadius: 12, padding: '12px 16px',
            marginBottom: 16, textAlign: 'center', fontSize: '0.9em',
            boxShadow: '0 4px 12px rgba(245,158,11,0.3)', animation: 'slideUp 0.4s ease',
          }}>
            <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
              {'\uD83D\uDD04'} Selfie Re-upload Required
            </div>
            <div style={{ color: '#78350f', fontSize: '0.85em' }}>
              Your selfie was rejected by the reviewer. Please take a new selfie below.
            </div>
          </div>
        )}

        {/* ══ SUNDAY BANNER ══ */}
        {isSunday && (
          <div style={{
            background: 'linear-gradient(135deg,#fff3cd,#ffe082)',
            border: '1px solid #ffc107', borderRadius: 12, padding: '10px 16px',
            marginBottom: 16, textAlign: 'center', fontSize: '0.9em',
            boxShadow: '0 4px 12px rgba(255,193,7,0.3)', animation: 'slideUp 0.4s ease'
          }}>
            Today is <strong>Sunday</strong>. Attendance is optional and counts as overtime.
          </div>
        )}

        {/* ══ TODAY STATUS CARD ══ */}
        {todayRecord === undefined ? (
          <div style={{
            height: 120, borderRadius: 16, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)',
            backgroundSize: '800px 100%', animation: 'shimmer 1.4s infinite linear', marginBottom: 16
          }} />
        ) : (
          <div style={{
            background: hasSelfie
              ? 'linear-gradient(135deg,#e8f5e9,#c8e6c9)'
              : isCheckedIn
              ? 'linear-gradient(135deg,#fff3e0,#ffe0b2)'
              : isCompleted
              ? 'linear-gradient(135deg,#e8f5e9,#c8e6c9)'
              : 'linear-gradient(135deg,#fafafa,#f0f0f0)',
            borderRadius: 18, padding: '18px 20px', marginBottom: 18,
            boxShadow: (hasSelfie || isCompleted)
              ? '0 8px 24px rgba(67,160,71,0.25), 0 2px 6px rgba(0,0,0,0.08)'
              : isCheckedIn
              ? '0 8px 24px rgba(230,81,0,0.25), 0 2px 6px rgba(0,0,0,0.08)'
              : '0 4px 16px rgba(0,0,0,0.1)',
            animation: 'slideUp 0.4s ease',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#333' }}>Today's Status</span>
              <span style={{
                padding: '4px 12px', borderRadius: 20, fontSize: '0.78em', fontWeight: 700,
                background: hasSelfie ? '#43a047' : isCompleted ? '#43a047' : isCheckedIn ? '#e65100' : '#9e9e9e',
                color: '#fff'
              }}>
                {hasSelfie ? 'Attendance Marked' : isCompleted ? (todayRecord?.selfieUrl ? 'Done' : 'Done (No Selfie)') : isCheckedIn ? 'Selfie Required' : 'Not In'}
              </span>
            </div>

            {!todayRecord ? (
              <div style={{ color: '#888', fontSize: '0.9em' }}>Tap "Check In" below to mark your attendance.</div>
            ) : (
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Selfie thumbnails (check-in + checkout) */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {hasSelfie && (
                    <div style={{
                      width: 52, height: 52, borderRadius: 10, overflow: 'hidden',
                      border: '2px solid #43a047', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      position: 'relative',
                    }}>
                      <SecureImg
                        src={`${process.env.REACT_APP_URL}/myattendance/selfie-image/${todayRecord.date}/${todayRecord.username}`}
                        alt="Check-in"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.55em', textAlign: 'center', padding: '1px 0' }}>IN</div>
                    </div>
                  )}
                  {todayRecord.checkoutSelfieUrl && (
                    <div style={{
                      width: 52, height: 52, borderRadius: 10, overflow: 'hidden',
                      border: '2px solid #ef5350', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      position: 'relative',
                    }}>
                      <SecureImg
                        src={`${process.env.REACT_APP_URL}/myattendance/selfie-image/${todayRecord.date}/${todayRecord.username}?type=checkout`}
                        alt="Checkout"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.55em', textAlign: 'center', padding: '1px 0' }}>OUT</div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', flex: 1 }}>
                  {[
                    ['Branch',    todayRecord.branchName || todayRecord.branch],
                    ['Check-in',  fmtTime(todayRecord.checkInTime)],
                    ['Check-out', fmtTime(todayRecord.checkOutTime)],
                    canSeeHours && ['Worked', todayRecord.workedMinutes != null
                      ? `${Math.floor(todayRecord.workedMinutes/60)}h ${todayRecord.workedMinutes%60}m`
                      : '—'],
                  ].filter(Boolean).map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize: '0.7em', color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
                      <div style={{ fontWeight: 600, fontSize: '0.95em', color: '#222' }}>{val}</div>
                    </div>
                  ))}
                  {todayRecord.isSunday && (
                    <div style={{ gridColumn: '1/-1', marginTop: 4 }}>
                      <span style={{ background: '#ff9800', color: '#fff', borderRadius: 10, padding: '2px 10px', fontSize: '0.78em', fontWeight: 700 }}>Overtime</span>
                    </div>
                  )}
                  {todayRecord.autoCheckout && (
                    <div style={{ gridColumn: '1/-1', marginTop: 4 }}>
                      <span style={{ background: '#e53935', color: '#fff', borderRadius: 10, padding: '2px 10px', fontSize: '0.78em', fontWeight: 700 }}>System Auto-Checkout</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ SELFIE MANDATORY BANNER ══ */}
        {needsSelfie && !selfieMode && !selfiePreview && !showWelcome && (
          <div style={{
            background: 'linear-gradient(135deg,#fff3e0,#ffe0b2)',
            border: '2px solid #e65100', borderRadius: 14, padding: '14px 18px',
            marginBottom: 16, textAlign: 'center',
            boxShadow: '0 4px 16px rgba(230,81,0,0.25)', animation: 'slideUp 0.4s ease',
          }}>
            <div style={{ fontWeight: 800, color: '#bf360c', fontSize: '1rem', marginBottom: 4 }}>
              Selfie Required for Attendance
            </div>
            <div style={{ color: '#4e342e', fontSize: '0.85em', lineHeight: 1.5 }}>
              Your attendance will not be counted until you upload a selfie. Please take a selfie now.
            </div>
          </div>
        )}

        {/* ══ ACTION AREA ══ */}
        {todayRecord !== undefined && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>

            {/* Not checked in → Check In button */}
            {!isCheckedIn && !isCompleted && (
              <button
                onClick={handleCheckIn}
                disabled={busy}
                style={{
                  padding: '16px', fontSize: '1.1rem', borderRadius: 14, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer', fontWeight: 700,
                  background: 'linear-gradient(135deg,#43a047,#2e7d32)',
                  color: '#fff',
                  boxShadow: '0 6px 20px rgba(67,160,71,0.4)',
                  animation: !busy ? 'pulseRing 2s infinite' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {busy ? 'Checking in...' : 'Check In'}
              </button>
            )}

            {/* Checked in → selfie button (take or retake for multi-shift) */}
            {isCheckedIn && !selfieMode && !selfiePreview && !showWelcome && (
              <button
                onClick={() => setShowWelcome(true)}
                style={{
                  padding: '14px', fontSize: '1rem', borderRadius: 14, border: 'none',
                  cursor: 'pointer', fontWeight: 700,
                  background: hasSelfie
                    ? 'linear-gradient(135deg,#7c3aed,#5b21b6)'
                    : 'linear-gradient(135deg,#1976d2,#0d47a1)',
                  color: '#fff', boxShadow: hasSelfie
                    ? '0 6px 20px rgba(124,58,237,0.4)'
                    : '0 6px 20px rgba(25,118,210,0.4)',
                }}
              >
                {hasSelfie ? 'Retake Selfie' : 'Take Selfie for Attendance'}
              </button>
            )}

            {/* Check Out button — enabled after 30 min since check-in */}
            {isCheckedIn && !checkoutSelfieMode && (
              <button
                onClick={handleCheckOut}
                disabled={busy || !checkoutAllowed}
                style={{
                  padding: '15px', fontSize: '1.05rem', borderRadius: 14, border: 'none',
                  cursor: (busy || !checkoutAllowed) ? 'not-allowed' : 'pointer', fontWeight: 700,
                  background: checkoutAllowed
                    ? 'linear-gradient(135deg,#ef5350,#c62828)'
                    : 'linear-gradient(135deg,#9e9e9e,#757575)',
                  color: '#fff', boxShadow: checkoutAllowed ? '0 6px 20px rgba(239,83,80,0.4)' : 'none',
                  opacity: checkoutAllowed ? 1 : 0.7,
                  transition: 'all 0.2s ease',
                }}
              >
                {busy ? 'Checking out...' : checkoutAllowed ? 'Check Out' : `Check Out (wait ${30 - minsSinceCheckIn}m)`}
              </button>
            )}

            {/* Completed → Check In Again button for multi-shift */}
            {isCompleted && (
              <button
                onClick={handleCheckIn}
                disabled={busy}
                style={{
                  padding: '14px', fontSize: '1rem', borderRadius: 14, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer', fontWeight: 700,
                  background: 'linear-gradient(135deg,#43a047,#2e7d32)',
                  color: '#fff', boxShadow: '0 4px 14px rgba(67,160,71,0.3)',
                  transition: 'all 0.2s ease',
                }}
              >
                {busy ? 'Checking in...' : 'Check In Again'}
              </button>
            )}

            {/* Shift history */}
            {todayRecord?.shifts?.length >= 1 && (
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.78em', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Shifts Today</div>
                {todayRecord.shifts.map((s, i) => {
                  const shiftMins = s.minutes || (s.checkOut ? Math.round((new Date(s.checkOut) - new Date(s.checkIn)) / 60000) : null);
                  return (
                    <div key={s._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: i < todayRecord.shifts.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                      <span style={{ fontSize: '0.85em', color: '#334155' }}>Shift {i + 1}</span>
                      <span style={{ fontSize: '0.85em', color: '#475569' }}>
                        {fmtTime(s.checkIn)} – {s.checkOut ? fmtTime(s.checkOut) : 'ongoing'}
                      </span>
                      <span style={{ fontSize: '0.8em', color: '#64748b', minWidth: 50, textAlign: 'right' }}>
                        {shiftMins != null ? `${Math.floor(shiftMins / 60)}h ${shiftMins % 60}m` : '...'}
                      </span>
                    </div>
                  );
                })}
                {todayRecord.workedMinutes != null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '2px solid #cbd5e1' }}>
                    <span style={{ fontSize: '0.85em', fontWeight: 700, color: '#1e293b' }}>Total</span>
                    <span style={{ fontSize: '0.85em', fontWeight: 700, color: '#1e293b' }}>
                      {Math.floor(todayRecord.workedMinutes / 60)}h {todayRecord.workedMinutes % 60}m
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ══ WELCOME POPUP (before selfie) ══ */}
        {showWelcome && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            animation: 'popIn 0.3s ease',
          }}>
            <div style={{
              background: '#fff', borderRadius: 20, padding: '28px 24px', maxWidth: 360,
              width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: 8 }}>{'\uD83D\uDE4F'}</div>
              <h3 style={{ margin: '0 0 12px', fontSize: '1.2rem', color: '#1a237e', fontWeight: 700 }}>
                Welcome, {displayName}!
              </h3>
              <p style={{ fontSize: '0.92em', color: '#555', lineHeight: 1.6, margin: '0 0 20px' }}>
                Please click and share a selfie with proper uniform compliance to mark your attendance.
              </p>
              <button
                onClick={() => { setShowWelcome(false); startSelfieCamera(); }}
                style={{
                  padding: '12px 32px', fontSize: '1rem', borderRadius: 12, border: 'none',
                  background: 'linear-gradient(135deg,#1976d2,#0d47a1)',
                  color: '#fff', fontWeight: 700, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(25,118,210,0.3)',
                }}
              >
                Open Camera
              </button>
            </div>
          </div>
        )}

        {/* ══ SELFIE CAMERA VIEWFINDER ══ */}
        {selfieMode && !selfiePreview && (
          <div style={{
            position: 'relative', marginBottom: 16, borderRadius: 16, overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)', animation: 'popIn 0.3s ease',
          }}>
            <video
              ref={videoRef}
              playsInline
              muted
              style={{
                width: '100%', display: 'block', maxHeight: '60vh', objectFit: 'cover',
                transform: 'scaleX(-1)',  // Mirror for selfie
              }}
            />
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '16px', textAlign: 'center',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            }}>
              <button
                onClick={captureSelfie}
                style={{
                  width: 64, height: 64, borderRadius: '50%', border: '4px solid #fff',
                  background: 'rgba(255,255,255,0.3)', cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                }}
              />
              <div style={{ color: '#fff', fontSize: '0.8em', marginTop: 6, fontWeight: 600 }}>
                Tap to capture
              </div>
            </div>
          </div>
        )}

        {/* ══ SELFIE PREVIEW + CONFIRM/RETAKE ══ */}
        {selfiePreview && (
          <div style={{
            marginBottom: 16, borderRadius: 16, overflow: 'hidden',
            boxShadow: '0 8px 28px rgba(0,0,0,0.2)', animation: 'slideUp 0.3s ease',
          }}>
            <img src={selfiePreview} alt="Selfie preview" style={{ width: '100%', display: 'block', maxHeight: '50vh', objectFit: 'cover' }} />
            <div style={{ display: 'flex', gap: 10, padding: 12, background: '#f8f9fa', justifyContent: 'center' }}>
              <button
                onClick={retakeSelfie}
                disabled={uploading}
                style={{
                  padding: '10px 24px', borderRadius: 10, border: '1.5px solid #e0e0e0',
                  background: '#fff', fontWeight: 600, fontSize: '0.9em', cursor: 'pointer',
                }}
              >
                Retake
              </button>
              <button
                onClick={handleUploadSelfie}
                disabled={uploading}
                style={{
                  padding: '10px 24px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg,#43a047,#2e7d32)',
                  color: '#fff', fontWeight: 700, fontSize: '0.9em',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                }}
              >
                {uploading ? 'Uploading...' : 'Confirm & Upload'}
              </button>
            </div>
          </div>
        )}

        {/* ══ CHECKOUT SELFIE CAMERA ══ */}
        {checkoutSelfieMode && selfieMode && !checkoutSelfiePreview && (
          <div style={{
            position: 'relative', marginBottom: 16, borderRadius: 16, overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)', animation: 'popIn 0.3s ease',
          }}>
            <div style={{
              background: 'linear-gradient(135deg,#ef5350,#c62828)',
              padding: '8px 16px', color: '#fff', fontWeight: 700, fontSize: '0.9em', textAlign: 'center',
            }}>
              Checkout Selfie
            </div>
            <video
              ref={videoRef}
              playsInline
              muted
              style={{
                width: '100%', display: 'block', maxHeight: '55vh', objectFit: 'cover',
                transform: 'scaleX(-1)',
              }}
            />
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '16px', textAlign: 'center',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                <button
                  onClick={cancelCheckoutSelfie}
                  style={{
                    padding: '10px 20px', borderRadius: 10, border: '2px solid #fff',
                    background: 'rgba(0,0,0,0.3)', color: '#fff', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={captureCheckoutSelfie}
                  style={{
                    width: 64, height: 64, borderRadius: '50%', border: '4px solid #fff',
                    background: 'rgba(239,83,80,0.5)', cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                  }}
                />
              </div>
              <div style={{ color: '#fff', fontSize: '0.8em', marginTop: 6, fontWeight: 600 }}>
                Tap to capture checkout selfie
              </div>
            </div>
          </div>
        )}

        {/* ══ CHECKOUT SELFIE PREVIEW ══ */}
        {checkoutSelfieMode && checkoutSelfiePreview && (
          <div style={{
            marginBottom: 16, borderRadius: 16, overflow: 'hidden',
            boxShadow: '0 8px 28px rgba(0,0,0,0.2)', animation: 'slideUp 0.3s ease',
          }}>
            <div style={{
              background: 'linear-gradient(135deg,#ef5350,#c62828)',
              padding: '8px 16px', color: '#fff', fontWeight: 700, fontSize: '0.9em', textAlign: 'center',
            }}>
              Confirm Checkout Selfie
            </div>
            <img src={checkoutSelfiePreview} alt="Checkout selfie" style={{ width: '100%', display: 'block', maxHeight: '50vh', objectFit: 'cover' }} />
            <div style={{ display: 'flex', gap: 10, padding: 12, background: '#f8f9fa', justifyContent: 'center' }}>
              <button
                onClick={cancelCheckoutSelfie}
                disabled={busy}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e0e0e0',
                  background: '#fff', fontWeight: 600, fontSize: '0.9em', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={retakeCheckoutSelfie}
                disabled={busy}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e0e0e0',
                  background: '#fff', fontWeight: 600, fontSize: '0.9em', cursor: 'pointer',
                }}
              >
                Retake
              </button>
              <button
                onClick={handleCheckOutWithSelfie}
                disabled={busy}
                style={{
                  padding: '10px 24px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg,#ef5350,#c62828)',
                  color: '#fff', fontWeight: 700, fontSize: '0.9em',
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? 'Checking out...' : 'Confirm & Check Out'}
              </button>
            </div>
          </div>
        )}

        {/* ══ STATUS MESSAGE ══ */}
        {statusMsg && (
          <div style={{
            padding: '12px 16px', borderRadius: 12, marginBottom: 14,
            background: isOkMsg ? 'linear-gradient(135deg,#e8f5e9,#c8e6c9)'
                       : isErrMsg ? 'linear-gradient(135deg,#ffebee,#ffcdd2)'
                       : 'linear-gradient(135deg,#e3f2fd,#bbdefb)',
            border: `1.5px solid ${isOkMsg ? '#81c784' : isErrMsg ? '#e57373' : '#90caf9'}`,
            fontWeight: 600, fontSize: '0.93em',
            animation: isErrMsg ? 'shake 0.4s ease' : 'slideUp 0.3s ease',
          }}>
            {statusMsg}
          </div>
        )}

        {/* ══ ATTENDANCE CALENDAR ══ */}
        <div style={{
          background: '#333', borderRadius: 18, padding: '16px',
          boxShadow: '0 8px 28px rgba(0,0,0,0.09), 0 2px 6px rgba(0,0,0,0.06)',
          animation: 'slideUp 0.5s ease', fontSize: '18px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <button onClick={() => setCalOffset(o => o - 1)}
              style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: '1rem' }}>
              {'\u2039'}
            </button>
            <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.95em' }}>{monthLabel}</span>
            <button onClick={() => setCalOffset(o => Math.min(o + 1, 0))} disabled={calOffset === 0}
              style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '4px 10px',
                cursor: calOffset === 0 ? 'default' : 'pointer', fontSize: '1rem', opacity: calOffset === 0 ? 0.3 : 1 }}>
              {'\u203A'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3, marginBottom: 4 }}>
            {['S','M','T','W','T','F','S'].map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: '0.7em', fontWeight: 700,
                color: i === 0 ? '#ff9800' : i === 6 ? '#9c27b0' : '#888', paddingBottom: 4 }}>{d}</div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3 }}>
            {days.map((dateStr, idx) => {
              if (!dateStr) return <div key={idx} />;
              const meta = dayMeta(dateStr);
              const dayNum = parseInt(dateStr.slice(-2), 10);
              return (
                <div key={dateStr} title={meta.tip} style={{
                  aspectRatio: '1', borderRadius: 8, background: meta.bg,
                  border: meta.border || (dateStr === todayStr ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.06)'),
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  boxShadow: (meta.bg !== 'transparent' && !meta.bg?.includes('transparent')) ? '0 2px 6px rgba(0,0,0,0.1)' : 'none',
                  transform: dateStr === todayStr ? 'scale(1.05)' : 'none', transition: 'transform 0.15s ease',
                }}>
                  <div style={{ fontSize: '0.6em', color: meta.color, opacity: 0.7, lineHeight: 1 }}>{dayNum}</div>
                  <div style={{ fontSize: '0.75em', fontWeight: 700, color: meta.color, lineHeight: 1.2 }}>{meta.icon}</div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 14, fontSize: '0.72em', color: '#666' }}>
            {[
              { bg: '#43a047', label: 'Present' },
              { bg: '#1976d2', label: 'Active' },
              { bg: '#ffebee', label: 'Absent', color: '#e53935' },
              { bg: '#fff3e0', label: 'Sunday', color: '#ff9800' },
              { bg: '#f3e5f5', label: 'Saturday', color: '#9c27b0' },
            ].map(({ bg, label, color }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: bg, border: `1px solid ${color || bg}` }} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* ══ QUICK STATS ══ */}
        {myHistory.length > 0 && (() => {
          const thisMonth = myHistory.filter(r => r.date.startsWith(todayStr.slice(0, 7)));
          const present   = thisMonth.filter(r => r.status === 'COMPLETED').length;
          const totalMins = canSeeHours ? thisMonth.reduce((s, r) => s + (r.workedMinutes || 0), 0) : 0;
          const stats = [
            { label: 'This Month', value: present, unit: 'days', color: '#43a047' },
            canSeeHours && { label: 'Total Hours', value: `${Math.floor(totalMins/60)}h`, unit: `${totalMins%60}m`, color: '#1976d2' },
            { label: 'OT Days', value: thisMonth.filter(r => r.isSunday).length, unit: 'Sundays', color: '#ff9800' },
          ].filter(Boolean);
          return (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length},1fr)`, gap: 10, marginTop: 14, animation: 'slideUp 0.6s ease' }}>
              {stats.map(({ label, value, unit, color }) => (
                <div key={label} style={{
                  background: '#fff', borderRadius: 14, padding: '12px 8px', textAlign: 'center',
                  boxShadow: `0 4px 16px rgba(0,0,0,0.08), inset 0 -3px 0 ${color}`,
                }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color }}>{value}</div>
                  <div style={{ fontSize: '0.65em', color: '#888', lineHeight: 1.1 }}>{unit}<br />{label}</div>
                </div>
              ))}
            </div>
          );
        })()}

      </div>
      )}
    </>
  );
}
